/**
 * Agent configuration for display and styling.
 *
 * This registry defines how nested agent activity is displayed in the UI.
 * When adding a new specialist agent:
 * 1. Add entry here with unique key matching agent_name from backend
 * 2. Agent will automatically be supported in UI
 */

export interface AgentConfig {
  /** Display name shown in UI */
  displayName: string;
  /** Tailwind color classes for styling */
  colors: {
    bg: string;
    border: string;
    text: string;
    bgLight: string;
  };
  /** Icon name (from lucide-react) */
  icon: 'brain' | 'chart-bar' | 'play-circle' | 'users';
}

/**
 * Registry of all specialist agents.
 *
 * To add a new agent:
 * 1. Add entry here with unique key matching agent_name from backend
 * 2. Agent will automatically be supported in UI
 */
export const AGENT_REGISTRY: Record<string, AgentConfig> = {
  stats_analyst: {
    displayName: 'Stats Analyst',
    colors: {
      bg: 'bg-purple-100',
      border: 'border-purple-300',
      text: 'text-purple-700',
      bgLight: 'bg-purple-50',
    },
    icon: 'chart-bar',
  },
  moments_analyst: {
    displayName: 'Moments Analyst',
    colors: {
      bg: 'bg-blue-100',
      border: 'border-blue-300',
      text: 'text-blue-700',
      bgLight: 'bg-blue-50',
    },
    icon: 'play-circle',
  },
};

/** Default config for unknown agents */
export const DEFAULT_AGENT_CONFIG: AgentConfig = {
  displayName: 'Specialist Agent',
  colors: {
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    text: 'text-gray-700',
    bgLight: 'bg-gray-50',
  },
  icon: 'users',
};

/**
 * Get agent config by name. Returns default if not found.
 */
export function getAgentConfig(agentName?: string): AgentConfig {
  if (!agentName) return DEFAULT_AGENT_CONFIG;
  return AGENT_REGISTRY[agentName] ?? DEFAULT_AGENT_CONFIG;
}

/**
 * Check if a tool is a delegation tool (spawns nested agent).
 */
export function isDelegationTool(toolName: string): boolean {
  return toolName.startsWith('delegate_to_');
}
